import { useCallback, useEffect, useState } from "react";
import { calibrationService, DEFAULT_GROUP_KEY } from "../../../../services/CalibrationService";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Switch from "@mui/material/Switch";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import { generateSensorFieldPlanDocx } from "./SensorFieldPlanDocx";
import { generatePickList } from "./PickListCsv";
import {
    COMPASS_GRID,
    normalizeCardinalDirections,
    getSpanKeyDirections,
    defaultAllowedMastHeadings,
} from "./cardinalDirectionUtils";
import { FormControlLabel } from "@mui/material";

// rssi thresholds defined for cell comms analysis, used to color code tiers
export const EXCELLENT_TIER_THRESHOLD = -90;
export const GOOD_TIER_THRESHOLD = -105;
export const FAIR_TIER_THRESHOLD = -120;

export function getCellCommsColor(rssi) {
    if (rssi == null) return "#c62828";
    if (rssi > EXCELLENT_TIER_THRESHOLD) return "#2e7d32";
    if (rssi > GOOD_TIER_THRESHOLD) return "#558b2f";
    if (rssi > FAIR_TIER_THRESHOLD) return "#f57f17";
    return "#c62828";
}

const SENSOR_FIELD_PLAN_DATASET_TYPE = "sensor-field-plan";
const KMZ_DATASET_TYPE = "sensor-placement-selection-kmz";

const PROJECT_REQUIRED_FIELDS = ["pv_angle", "use_bespoke_sim", "power_module"];

const POWER_MODULE_OPTIONS = ["50W", "100W"];
const BANDING_TYPE_OPTIONS = {"201SS": "201 Stainless Steel", "316SS": "316 Stainless Steel"};
const num_banding_cutters_OPTIONS = [1, 2, 3, 4];

const TOWER_TYPES = ["Lattice", "H-Frame", "Wood Monopole", "Steel Monopole"];
const TOWER_TYPES_WITHOUT_LEGS = ["H-Frame", "Wood Monopole", "Steel Monopole"];

function isProjectConfigured(details, { allSensorsLattice = false, allSensorsNonLattice = false } = {}) {
    if (details == null) return false;
    if (!PROJECT_REQUIRED_FIELDS.every((f) => details[f] !== null && details[f] !== undefined)) return false;
    if (!allSensorsLattice && (details.banding_type == null || details.num_banding_cutters == null)) return false;
    if (!allSensorsNonLattice && details.use_large_lattice_kit == null) return false;
    return true;
}

function isSensorConfigured(userConfig) {
    if (!userConfig?.tower_type) return false;
    if (!userConfig?.allowed_mast_headings?.length) return false;
    if (!TOWER_TYPES_WITHOUT_LEGS.includes(userConfig?.tower_type) && !userConfig?.mounting_tower_leg) return false;
    if (TOWER_TYPES_WITHOUT_LEGS.includes(userConfig?.tower_type) && userConfig?.hilti_install_override == null) return false;
    return true;
}

function ConfigFlag({ configured }) {
    return configured
        ? <CheckCircleIcon style={{ color: "#4caf50", fontSize: 18, flexShrink: 0, marginLeft: 8 }} />
        : (
            <span style={{ display: "flex", alignItems: "center", gap: 3, marginLeft: 8, flexShrink: 0 }}>
                <CancelIcon style={{ color: "#f44336", fontSize: 18 }} />
                <span style={{ fontSize: 10, color: "#f44336", whiteSpace: "nowrap" }}>Details missing</span>
            </span>
        );
}

function FieldRow({ label, value, isLink, linkText, valueStyle }) {
    const isEmpty = value === null || value === undefined;
    return (
        <div style={{ display: "flex", gap: 12, padding: "5px 0", borderBottom: "1px solid #f0f0f0" }}>
            <span style={{ fontWeight: 600, minWidth: 200, color: "#555", fontSize: 13 }}>{label}</span>
            {isLink && !isEmpty
                ? <a href={value} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>{linkText ?? "Open in Maps"}</a>
                : <span style={{ fontSize: 13, color: isEmpty ? "#bbb" : "inherit", ...(valueStyle ?? {}) }}>{isEmpty ? "N/A" : String(value)}</span>
            }
        </div>
    );
}

function EditActions({ onEdit, onSave, onCancel, isEditing, isSaving, saveDisabled, editLabel = "Edit", saveLabel = "Save", editDisabled, editTooltip }) {
    return (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            {isEditing ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {isSaving && <CircularProgress size={16} />}
                    <Button size="small" onClick={onCancel} disabled={isSaving}>Cancel</Button>
                    <Button size="small" variant="contained" color="primary" onClick={onSave} disabled={isSaving || saveDisabled}>{saveLabel}</Button>
                </div>
            ) : (
                <Tooltip title={editTooltip ?? ""} arrow>
                    <span>
                        <Button size="small" startIcon={<EditIcon />} onClick={onEdit} disabled={editDisabled}>{editLabel}</Button>
                    </span>
                </Tooltip>
            )}
        </div>
    );
}

export default function InstallDetails({ client, project }) {
    const [placementOptionsData, setPlacementOptionsData] = useState(null);
    const [placementOptionsInputData, setPlacementOptionsInputData] = useState(null);
    const [fieldPlanData, setFieldPlanData] = useState(null);
    const [fieldPlanMeta, setFieldPlanMeta] = useState(null);
    const [expandedPanel, setExpandedPanel] = useState(null);
    const [editingPanel, setEditingPanel] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [spareFractionsOpen, setSpareFractionsOpen] = useState(false);
    const [spareFractionsValues, setSpareFractionsValues] = useState({});
    const [isDownloadingKmz, setIsDownloadingKmz] = useState(false);
    const [isCreatingFieldPlan, setIsCreatingFieldPlan] = useState(false);
    const [pendingDownload, setPendingDownload] = useState(null);

    const loadPlacementInputData = useCallback(async (placementData) => {
        if (!placementData) { setPlacementOptionsInputData(null); return null; }
        const inputDatasetMeta = placementData?.meta?.input_datasets?.find(
            (ds) => ds.dataset_type === "SensorPlacementInput"
        );
        if (inputDatasetMeta) {
            try {
                const inputData = await calibrationService.getExactDataset(
                    inputDatasetMeta.client_name,
                    inputDatasetMeta.project_name,
                    inputDatasetMeta.group_key,
                    "sensor-placement-input",
                    inputDatasetMeta.version_dttm.replace(/\.\d+([+-]\d{2}:\d{2}|Z)$/, "Z")
                );
                setPlacementOptionsInputData(inputData ?? null);
                return inputData ?? null;
            } catch {
                setPlacementOptionsInputData(null);
                return null;
            }
        } else {
            setPlacementOptionsInputData(null);
            return null;
        }
    }, []);

    const loadData = useCallback(async () => {
        if (!client || !project) {
            setPlacementOptionsData(null);
            setPlacementOptionsInputData(null);
            setFieldPlanData(null);
            setFieldPlanMeta(null);
            return null;
        }
        try {
            const [placementData, fieldPlan] = await Promise.all([
                calibrationService.getLatestDatasetViaSignedUrl(client, project, DEFAULT_GROUP_KEY, "sensor-placement-options", false),
                calibrationService.getLatestDatasetViaSignedUrl(client, project, DEFAULT_GROUP_KEY, SENSOR_FIELD_PLAN_DATASET_TYPE).catch(() => null),
            ]);
            setPlacementOptionsData(placementData ?? null);
            setFieldPlanData(fieldPlan?.data ?? null);
            setFieldPlanMeta(fieldPlan?.meta ?? null);
            const inputData = await loadPlacementInputData(placementData);
            return { placement: placementData ?? null, inputData };
        } catch {
            setPlacementOptionsData(null);
            setPlacementOptionsInputData(null);
            setFieldPlanData(null);
            setFieldPlanMeta(null);
            return null;
        }
    }, [client, project, loadPlacementInputData]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleProjectChange = (_e, isExpanded) => {
        if (editingPanel !== null) return;
        setExpandedPanel(isExpanded ? "project" : null);
    };

    const handleSensorChange = (sensorName) => (_e, isExpanded) => {
        if (editingPanel !== null) return;
        setExpandedPanel(isExpanded ? sensorName : null);
    };

    const startEdit = (panelId, initialValues) => {
        setEditingPanel(panelId);
        setEditValues(initialValues);
    };

    const cancelEdit = () => {
        setEditingPanel(null);
        setEditValues({});
    };

    const setField = (key, value) => setEditValues((v) => ({ ...v, [key]: value }));

    const saveProjectEdit = useCallback(async () => {
        setIsSaving(true);
        try {
            await calibrationService.submitSensorPlacementRefinement(
                client, project,
                placementOptionsData?.meta?.version_dttm,
                placementOptionsInputData?.meta?.version_dttm,
                {
                    newly_edited_project_installation_details: {
                        pv_angle: editValues.pv_angle,
                        power_module: editValues.power_module,
                        use_bespoke_sim: editValues.use_bespoke_sim,
                        spare_fractions: editValues.spare_fractions,
                        banding_type: editValues.banding_type,
                        num_banding_cutters: editValues.num_banding_cutters,
                        use_large_lattice_kit: editValues.use_large_lattice_kit,
                    },
                }
            );
            await loadData();
            setEditingPanel(null);
            setEditValues({});
        } finally {
            setIsSaving(false);
        }
    }, [client, project, placementOptionsData, placementOptionsInputData, editValues, loadData]);

    const saveSensorEdit = useCallback(async (sensorName) => {
        const structureName = placementOptionsData?.data?.sensor_name_structure?.[sensorName];
        const placementConfig = placementOptionsData?.data?.structure_configs?.[structureName]?.user_config;
        setIsSaving(true);
        try {
            await calibrationService.submitSensorPlacementRefinement(
                client, project,
                placementOptionsData?.meta?.version_dttm,
                placementOptionsInputData?.meta?.version_dttm,
                {
                    newly_accepted_sensors: {
                        [sensorName]: {
                            install_option_idx: placementConfig?.install_option_idx,
                            install_hag: placementConfig?.install_hag,
                            tower_type: editValues.tower_type,
                            allowed_mast_headings: editValues.allowed_mast_headings,
                            mounting_tower_leg: TOWER_TYPES_WITHOUT_LEGS.includes(editValues.tower_type) ? null : editValues.mounting_tower_leg,
                            hilti_install_override: TOWER_TYPES_WITHOUT_LEGS.includes(editValues.tower_type) ? (editValues.hilti_install_override ?? false) : false,
                        },
                    },
                }
            );
            await loadData();
            setEditingPanel(null);
            setEditValues({});
        } finally {
            setIsSaving(false);
        }
    }, [client, project, placementOptionsData, placementOptionsInputData, editValues, loadData]);

    const handleCreateFieldPlan = async () => {
        setIsCreatingFieldPlan(true);
        try {
            await calibrationService.createSensorFieldPlan(client, project, placementOptionsData?.meta?.version_dttm);
        } finally {
            await loadData();
            setIsCreatingFieldPlan(false);
        }
    };

    const handleDownloadKmz = async () => {
        setIsDownloadingKmz(true);
        try {
            const groupKey = fieldPlanMeta?.group_key;
            const files = await calibrationService.getFileNames(client, project, groupKey, KMZ_DATASET_TYPE);
            const latestVersion = [...(files?.data ?? [])].sort().pop();
            if (!latestVersion) return;
            const data = await calibrationService.getExactDataset(client, project, groupKey, KMZ_DATASET_TYPE, latestVersion, true, true);
            const blob = new Blob([data], { type: "application/vnd.google-earth.kmz" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${client}_${project}_sensor-placement.kmz`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            // error handled by service
        } finally {
            setIsDownloadingKmz(false);
        }
    };

    const sensorNameToStructure = placementOptionsData?.data?.sensor_name_structure ?? {};
    const structureConfigs = placementOptionsData?.data?.structure_configs ?? {};
    const sensorNameStatus = placementOptionsData?.data?.sensor_name_status ?? {};
    const structureToCellComms = placementOptionsData?.data?.structure_to_cell_comms ?? {};
    const projectDetails = placementOptionsData?.data?.project_installation_details;
    const requiredStructures = new Set(placementOptionsData?.data?.required_structures ?? []);

    const allSensorsConfigured = Object.entries(sensorNameToStructure).length > 0 &&
        Object.entries(sensorNameToStructure).every(([, structureName]) =>
            isSensorConfigured(structureConfigs[structureName]?.user_config)
        );

    const acceptedSensorStructures = Object.values(sensorNameToStructure).filter(
        (structureName) => structureConfigs[structureName]?.user_config?.tower_type != null
    );
    const allSensorsLattice = acceptedSensorStructures.length > 0 &&
        acceptedSensorStructures.every(
            (structureName) => structureConfigs[structureName]?.user_config?.tower_type === "Lattice"
        );
    const allSensorsNonLattice = acceptedSensorStructures.length > 0 &&
        acceptedSensorStructures.every(
            (structureName) => structureConfigs[structureName]?.user_config?.tower_type !== "Lattice"
        );
    const allSensorsHaveTowerType = Object.values(sensorNameToStructure).length > 0 &&
        Object.values(sensorNameToStructure).every(
            (structureName) => structureConfigs[structureName]?.user_config?.tower_type != null
        );


    const isEditingProject = editingPanel === "project";

    const isFieldPlanStale =
        fieldPlanMeta?.version_dttm != null &&
        placementOptionsData?.meta?.version_dttm != null &&
        new Date(fieldPlanMeta.version_dttm) < new Date(placementOptionsData.meta.version_dttm);

    const handleDownloadFieldPlanClick = () => {
        if (isFieldPlanStale) { setPendingDownload("field-plan"); return; }
        generateSensorFieldPlanDocx(client, project);
    };

    const handleDownloadKmzClick = () => {
        if (isFieldPlanStale) { setPendingDownload("kmz"); return; }
        handleDownloadKmz();
    };

    const handleDownloadPickListClick = () => {
        if (isFieldPlanStale) { setPendingDownload("pick-list"); return; }
        generatePickList(client, project);
    }

    const confirmPendingDownload = () => {
        if (pendingDownload === "field-plan") generateSensorFieldPlanDocx(client, project);
        if (pendingDownload === "kmz") handleDownloadKmz();
        if (pendingDownload == "pick-list") generatePickList(client, project);
        setPendingDownload(null);
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Dialog open={spareFractionsOpen} onClose={() => setSpareFractionsOpen(false)}>
                <DialogTitle>Edit Spare Fractions</DialogTitle>
                <DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
                    {[
                        { key: "rs1_sensors", label: "RS1 Sensors" },
                        { key: "weather_stations", label: "Weather Stations" },
                        { key: "lattice_mounting_kit", label: "Lattice Mounting Kit" },
                        { key: "monopole_mounting_kit", label: "Monopole Mounting Kit" },
                        { key: "banding_roll", label: "Banding Roll" },
                    ].map(({ key, label }) => (
                        <TextField
                            key={key}
                            label={label}
                            type="number"
                            value={spareFractionsValues[key] ?? ""}
                            onChange={(e) => {
                                const val = e.target.value === "" ? null : Number(e.target.value);
                                if (val === null || (val > 0 && val < 1)) {
                                    setSpareFractionsValues((v) => ({ ...v, [key]: val }));
                                }
                            }}
                            size="small"
                            inputProps={{ step: 0.01, min: 0.01, max: 0.99 }}
                            helperText="0–1"
                            fullWidth
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSpareFractionsOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setField("spare_fractions", spareFractionsValues);
                            setSpareFractionsOpen(false);
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={pendingDownload !== null} onClose={() => setPendingDownload(null)}>
                <DialogTitle>Outdated {pendingDownload === "kmz" ? "KMZ" : "Field Plan"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This {pendingDownload === "kmz" ? "KMZ" : "field plan"} is outdated.
                        Project and/or install details have been changed since it was last saved on {new Date(fieldPlanMeta?.version_dttm).toLocaleString()}.
                        Save a new field plan to get the latest changes.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDownload(null)}>Cancel</Button>
                    <Button variant="contained" onClick={confirmPendingDownload}>Download Anyway</Button>
                </DialogActions>
            </Dialog>

            {(fieldPlanData || fieldPlanMeta) && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                    {fieldPlanMeta?.version_dttm && (
                        <span style={{ fontSize: 11, color: "#888" }}>
                            Last saved: {new Date(fieldPlanMeta.version_dttm).toLocaleString()}
                        </span>
                    )}
                    <div style={{ display: "flex", gap: 32 }}>
                        {fieldPlanData && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadFieldPlanClick}
                            >
                                Download Field Plan
                            </Button>
                        )}
                        {fieldPlanMeta && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={isDownloadingKmz ? <CircularProgress size={14} /> : <DownloadIcon />}
                                onClick={handleDownloadKmzClick}
                                disabled={isDownloadingKmz}
                            >
                                Download KMZ
                            </Button>
                        )}

                        {fieldPlanMeta && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadPickListClick}
                            >
                                Download Pick List
                            </Button>
                        )}


                    </div>
                </div>
            )}
            

            {placementOptionsData?.meta?.version_dttm && (
                <span style={{ fontSize: 11, color: "#888" }}>
                    Sensor Options were last updated: {new Date(placementOptionsData.meta.version_dttm).toLocaleString()}
                </span>
            )}
            {projectDetails && (
                <>
                    <div style={{ marginTop: 4, marginBottom: 4 }}>
                        <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#333" }}>
                            Project Details
                        </Typography>
                    </div>
                    <Accordion expanded={expandedPanel === "project"} onChange={handleProjectChange} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ minHeight: 56 }}>
                            <Typography variant="h6" style={{ flexGrow: 1, fontSize: 16, fontWeight: 600 }}>
                                Project Installation Details
                            </Typography>
                            <ConfigFlag configured={isProjectConfigured(projectDetails, { allSensorsLattice, allSensorsNonLattice })} />
                        </AccordionSummary>
                        <AccordionDetails style={{ display: "flex", flexDirection: "column" }}>
                            <EditActions
                                isEditing={isEditingProject}
                                isSaving={isSaving}
                                saveDisabled={false}
                                onEdit={() => startEdit("project", {
                                    pv_angle: projectDetails.pv_angle,
                                    power_module: projectDetails.power_module ?? null,
                                    use_bespoke_sim: projectDetails.use_bespoke_sim ?? false,
                                    banding_type: projectDetails.banding_type ?? null,
                                    num_banding_cutters: projectDetails.num_banding_cutters ?? null,
                                    spare_fractions: projectDetails.spare_fractions ?? null,
                                    use_large_lattice_kit: projectDetails.use_large_lattice_kit ?? false,
                                })}
                                onSave={saveProjectEdit}
                                onCancel={cancelEdit}
                            />
                            {isEditingProject ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
                                    <TextField
                                        label="PV Angle"
                                        type="number"
                                        value={editValues.pv_angle ?? ""}
                                        onChange={(e) => setField("pv_angle", e.target.value === "" ? null : Number(e.target.value))}
                                        size="small"
                                        fullWidth
                                    />
                                    <TextField
                                        select
                                        label="Power Module"
                                        value={editValues.power_module ?? ""}
                                        onChange={(e) => setField("power_module", e.target.value || null)}
                                        size="small"
                                        fullWidth
                                    >
                                        <MenuItem value="">Unknown</MenuItem>
                                        {POWER_MODULE_OPTIONS.map((v) => (
                                            <MenuItem key={v} value={v}>{v}</MenuItem>
                                        ))}
                                    </TextField>
                                    <Tooltip title={!allSensorsHaveTowerType ? "All sensors must have a tower type selected before editing banding fields" : allSensorsLattice ? "Banding type is not applicable when all sensors are on Lattice towers" : ""} arrow>
                                        <span>
                                            <TextField
                                                select
                                                label="Banding Type"
                                                value={!allSensorsHaveTowerType || allSensorsLattice ? "" : (editValues.banding_type ?? "")}
                                                onChange={(e) => setField("banding_type", e.target.value || null)}
                                                size="small"
                                                fullWidth
                                                disabled={!allSensorsHaveTowerType || allSensorsLattice}
                                            >
                                                <MenuItem value="">Unknown</MenuItem>
                                                {Object.entries(BANDING_TYPE_OPTIONS).map(([k, v]) => (
                                                    <MenuItem key={k} value={v}>{k}</MenuItem>
                                                ))}
                                            </TextField>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={!allSensorsHaveTowerType ? "All sensors must have a tower type selected before editing banding fields" : allSensorsLattice ? "Banding cutters are not applicable when all sensors are on Lattice towers" : ""} arrow>
                                        <span>
                                            <TextField
                                                select
                                                label="Banding Cutters"
                                                value={!allSensorsHaveTowerType || allSensorsLattice ? "" : (editValues.num_banding_cutters ?? "")}
                                                onChange={(e) => setField("num_banding_cutters", e.target.value === "" ? null : Number(e.target.value))}
                                                size="small"
                                                fullWidth
                                                disabled={!allSensorsHaveTowerType || allSensorsLattice}
                                            >
                                                <MenuItem value="">Unknown</MenuItem>
                                                {num_banding_cutters_OPTIONS.map((v) => (
                                                    <MenuItem key={v} value={v}>{v}</MenuItem>
                                                ))}
                                            </TextField>
                                        </span>
                                    </Tooltip>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 4 }}>
                                        Custom Controls
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={editValues.use_bespoke_sim ?? false}
                                                    onChange={(e) => setField("use_bespoke_sim", e.target.checked)}
                                                />
                                            }
                                            label={
                                                <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#666" }}>
                                                    Bespoke SIM Card
                                                    <Tooltip title="This is needed in case a customer wants a specific SIM that is not the typical Soracom one" arrow>
                                                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: "50%", border: "1px solid #999", color: "#999", fontSize: 10, lineHeight: 1, cursor: "default", flexShrink: 0 }}>?</span>
                                                    </Tooltip>
                                                </span>
                                            }
                                            labelPlacement="end"
                                        />
                                        <Tooltip title={!allSensorsHaveTowerType ? "All sensors must have a tower type selected before editing large lattice kit" : ""} arrow>
                                            <span>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={!allSensorsHaveTowerType ? false : (editValues.use_large_lattice_kit ?? false)}
                                                            onChange={(e) => setField("use_large_lattice_kit", e.target.checked)}
                                                            disabled={!allSensorsHaveTowerType}
                                                        />
                                                    }
                                                    label={
                                                        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#666" }}>
                                                            Large Lattice Kit
                                                            <Tooltip title="This is needed in case to override in NGET and other specific customers with large lattice towers requiring different kits" arrow>
                                                                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: "50%", border: "1px solid #999", color: "#999", fontSize: 10, lineHeight: 1, cursor: "default", flexShrink: 0 }}>?</span>
                                                            </Tooltip>
                                                        </span>
                                                    }
                                                    labelPlacement="end"
                                                />
                                            </span>
                                        </Tooltip>
                                        <div style={{ width: 1, height: 28, backgroundColor: "#e0e0e0", alignSelf: "center" }} />
                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            <span style={{ fontSize: 10, color: "#999", lineHeight: 1 }}>Spare Fractions</span>
                                            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                <span style={{ fontSize: 13, color: "#bbb", fontStyle: "italic" }}>
                                                    {editValues.spare_fractions ? "Custom" : "Default"}
                                                </span>
                                                <Tooltip title="Edit spare fractions" arrow>
                                                    <IconButton
                                                        size="small"
                                                        style={{ padding: 2 }}
                                                        onClick={() => {
                                                            setSpareFractionsValues(editValues.spare_fractions ?? {});
                                                            setSpareFractionsOpen(true);
                                                        }}
                                                    >
                                                        <EditIcon style={{ fontSize: 13, color: "#999" }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <FieldRow label="PV Angle" value={projectDetails.pv_angle} />
                                    <FieldRow label="Power Module" value={projectDetails.power_module} />
                                    <FieldRow label="Banding Type" value={projectDetails.banding_type} />
                                    <FieldRow label="Banding Cutters" value={projectDetails.num_banding_cutters} />
                                    <FieldRow label="Bespoke SIM Card" value={projectDetails.use_bespoke_sim != null ? (projectDetails.use_bespoke_sim ? "Yes" : "No") : null} />
                                    <FieldRow label="Large Lattice Kits" value={projectDetails.use_large_lattice_kit != null ? (projectDetails.use_large_lattice_kit ? "Yes" : "No") : null} />

                                </>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </>
            )}

            {Object.keys(sensorNameToStructure).length > 0 && (
                <Typography variant="subtitle1" style={{ fontWeight: 700, marginTop: 16, marginBottom: 4, color: "#333" }}>
                    Sensor Installations
                </Typography>
            )}
            {Object.entries(sensorNameToStructure).map(([sensorName, structureName]) => {
                const config = structureConfigs[structureName];
                const userConfig = config?.user_config;
                const installOption = config?.sorted_install_options?.[userConfig?.install_option_idx];
                const cellComms = structureToCellComms[structureName];
                const spanHeading = installOption?.span_heading;
                const keyDirs = spanHeading != null ? getSpanKeyDirections(spanHeading) : new Set();
                const isEditingSensor = editingPanel === sensorName;
                const status = sensorNameStatus[sensorName];

                return (
                    <Accordion key={sensorName} expanded={expandedPanel === sensorName} onChange={handleSensorChange(sensorName)} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography style={{ flexGrow: 1, fontSize: 14 }}>
                                <strong>{sensorName}</strong>
                                {" — "}
                                <span style={{ fontWeight: 400 }}>{structureName}</span>
                                {installOption?.line_name && (
                                    <span style={{ fontWeight: 400, color: "#777" }}>{" · "}{installOption.line_name}</span>
                                )}
                                {requiredStructures.has(structureName) && (
                                    <span style={{
                                        fontSize: 10,
                                        padding: "1px 5px",
                                        borderRadius: 4,
                                        marginLeft: 6,
                                        verticalAlign: "middle",
                                        whiteSpace: "nowrap",
                                        backgroundColor: "#ede7f6",
                                        color: "#5e35b1",
                                        border: "1px solid #5e35b1",
                                    }}>
                                        REQUIRED
                                    </span>
                                )}
                                {status && (
                                    <span style={{
                                        fontSize: 10,
                                        padding: "1px 5px",
                                        borderRadius: 4,
                                        marginLeft: 6,
                                        verticalAlign: "middle",
                                        whiteSpace: "nowrap",
                                        backgroundColor: status === "NO_DECISION" ? "#fff3e0" : status === "REJECTED" ? "#ffebee" : "#e8f5e9",
                                        color: status === "NO_DECISION" ? "#e65100" : status === "REJECTED" ? "#c62828" : "#2e7d32",
                                        border: `1px solid ${status === "NO_DECISION" ? "#e65100" : status === "REJECTED" ? "#c62828" : "#2e7d32"}`,
                                    }}>
                                        {status.replace(/_/g, " ")}
                                    </span>
                                )}
                            </Typography>
                            <ConfigFlag configured={isSensorConfigured(userConfig)} />
                        </AccordionSummary>
                        <AccordionDetails style={{ display: "flex", flexDirection: "column" }}>
                            <EditActions
                                isEditing={isEditingSensor}
                                isSaving={isSaving}
                                saveDisabled={false}
                                onEdit={() => startEdit(sensorName, {
                                    tower_type: userConfig?.tower_type ?? null,
                                    allowed_mast_headings: userConfig?.allowed_mast_headings?.length
                                        ? normalizeCardinalDirections(userConfig.allowed_mast_headings)
                                        : [...defaultAllowedMastHeadings(spanHeading)],
                                    mounting_tower_leg: userConfig?.mounting_tower_leg ?? null,
                                    hilti_install_override: userConfig?.hilti_install_override ?? false,
                                })}
                                onSave={() => saveSensorEdit(sensorName)}
                                onCancel={cancelEdit}
                                editDisabled={status !== "ACCEPTED"}
                                editTooltip={status !== "ACCEPTED" ? "Sensor must be accepted on the map view page before editing installation details" : ""}
                            />
                            <FieldRow label="Structure" value={structureName} />
                            {config?.structure_latitude != null && config?.structure_longitude != null && (
                                <FieldRow
                                    label="Location"
                                    value={`https://www.google.com/maps?q=${config.structure_latitude},${config.structure_longitude}`}
                                    isLink
                                    linkText={`${config.structure_latitude}, ${config.structure_longitude}`}
                                />
                            )}
                            {installOption?.line_name && <FieldRow label="Line Name" value={installOption.line_name} />}
                            {installOption?.span_end_structure_name && <FieldRow label="End Tower" value={installOption.span_end_structure_name} />}
                            <FieldRow label="Install HAG (m)" value={userConfig?.install_hag != null ? Number(userConfig.install_hag).toFixed(2) : null} />
                            <FieldRow label="Span Heading" value={spanHeading != null ? Math.round(spanHeading) : null} />
                            {isEditingSensor ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
                                    <div style={{ display: "flex", gap: 8 }}>
                                        <TextField
                                            select
                                            label="Tower Type"
                                            value={editValues.tower_type ?? ""}
                                            onChange={(e) => setField("tower_type", e.target.value || null)}
                                            size="small"
                                            style={{ flex: 1 }}
                                        >
                                            <MenuItem value="">Unknown</MenuItem>
                                            {TOWER_TYPES.map((v) => (
                                                <MenuItem key={v} value={v}>{v}</MenuItem>
                                            ))}
                                        </TextField>
                                        <Tooltip
                                            title={TOWER_TYPES_WITHOUT_LEGS.includes(editValues.tower_type) ? "H-Frame, Wood Monopole, and Steel Monopole tower types do not require a mounting leg" : ""}
                                            arrow
                                        >
                                            <span style={{ flex: 1, display: "block" }}>
                                                <TextField
                                                    label="Mounting Tower Leg"
                                                    value={TOWER_TYPES_WITHOUT_LEGS.includes(editValues.tower_type) ? "" : (editValues.mounting_tower_leg ?? "")}
                                                    disabled={TOWER_TYPES_WITHOUT_LEGS.includes(editValues.tower_type)}
                                                    onChange={(e) => setField("mounting_tower_leg", e.target.value || null)}
                                                    size="small"
                                                    fullWidth
                                                />
                                            </span>
                                        </Tooltip>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 12, color: "#666", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                                            Allowed Mast Headings
                                            <Tooltip title="The mast on which the anemometer is mounted should ideally be parallel or perpendicular to the span the camera is monitoring. Select the headings which are possible for install" arrow>
                                                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: "50%", border: "1px solid #999", color: "#999", fontSize: 10, lineHeight: 1, cursor: "default", flexShrink: 0 }}>?</span>
                                            </Tooltip>
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 26px)", gap: "2px" }}>
                                            {COMPASS_GRID.flatMap((row) =>
                                                row.map((dir) => {
                                                    if (dir === null) {
                                                        const hRad = spanHeading != null ? (spanHeading * Math.PI) / 180 : 0;
                                                        const L = 11, cx = 13, cy = 13;
                                                        return (
                                                            <Tooltip key="center" title={spanHeading != null ? `${spanHeading.toFixed(1)}°` : ""} arrow>
                                                                <div style={{ width: 26, height: 26 }}>
                                                                    <svg width="26" height="26" viewBox="0 0 26 26">
                                                                        <line
                                                                            x1={cx + Math.sin(hRad) * L} y1={cy - Math.cos(hRad) * L}
                                                                            x2={cx - Math.sin(hRad) * L} y2={cy + Math.cos(hRad) * L}
                                                                            stroke="#666" strokeWidth="2" strokeLinecap="round"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            </Tooltip>
                                                        );
                                                    }
                                                    const isAllowed = (editValues.allowed_mast_headings ?? []).includes(dir);
                                                    const isKeyDir = keyDirs.has(dir);
                                                    return (
                                                        <button
                                                            key={dir}
                                                            onClick={() => setField(
                                                                "allowed_mast_headings",
                                                                isAllowed
                                                                    ? (editValues.allowed_mast_headings ?? []).filter((d) => d !== dir)
                                                                    : [...(editValues.allowed_mast_headings ?? []), dir]
                                                            )}
                                                            style={{
                                                                width: 26, height: 26,
                                                                border: isAllowed && isKeyDir ? "1px solid #1976d2" : "1px solid #ccc",
                                                                borderRadius: 3,
                                                                backgroundColor: !isAllowed ? "#f5f5f5" : isKeyDir ? "#1976d2" : "#e0e0e0",
                                                                color: !isAllowed ? "#aaa" : isKeyDir ? "#fff" : "#555",
                                                                fontSize: 10, fontWeight: "bold", cursor: "pointer", padding: 0,
                                                                display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
                                                            }}
                                                        >
                                                            {!isAllowed ? "×" : dir}
                                                        </button>
                                                    );
                                                })
                                            )}
                                        </div>
                                        {TOWER_TYPES_WITHOUT_LEGS.includes(editValues.tower_type) && (
                                            <div>
                                                <FormControlLabel
                                                    control={<Switch checked={editValues.hilti_install_override ?? false} onChange={(e) => setField("hilti_install_override", e.target.checked)} />}
                                                    label={
                                                        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#666" }}>
                                                            Hilti Install Override
                                                            <Tooltip title="This override is needed when corten towers are identified after scoping in order to reflect hardware requirement changes in the pick list" arrow>
                                                                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: "50%", border: "1px solid #999", color: "#999", fontSize: 10, lineHeight: 1, cursor: "default", flexShrink: 0 }}>?</span>
                                                            </Tooltip>
                                                        </span>
                                                    }
                                                    labelPlacement="end"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <FieldRow label="Tower Type" value={userConfig?.tower_type} />
                                    <FieldRow label="Allowed Mast Headings" value={normalizeCardinalDirections(userConfig?.allowed_mast_headings).join(", ") || null} />
                                    <FieldRow label="Mounting Tower Leg" value={userConfig?.mounting_tower_leg} />
                                    {TOWER_TYPES_WITHOUT_LEGS.includes(userConfig?.tower_type) && <FieldRow label="Hilti Install Override" value={userConfig?.hilti_install_override != null ? (userConfig.hilti_install_override ? "Yes" : "No") : null} />}
                                    {cellComms && (
                                        <>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, marginBottom: 2 }}>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>Cell Comms Availability</span>
                                                <div style={{ flex: 1, height: 1, backgroundColor: "#e0e0e0" }} />
                                            </div>
                                            {cellComms.region === "UK" && (
                                                <>
                                                    <FieldRow label="UK Cell Provider" value={cellComms.uk_cell_provider} />
                                                    <FieldRow label="Nearest Cell Tower Distance (km)" value={cellComms.uk_nearest_cell_tower_distance_km != null ? Number(cellComms.uk_nearest_cell_tower_distance_km).toFixed(2) : null} valueStyle={cellComms.uk_nearest_cell_tower_distance_km != null ? { color: cellComms.uk_nearest_cell_tower_distance_km < 10 ? "#2e7d32" : null, fontWeight: 600 } : undefined} />
                                                </>
                                            )}
                                            {cellComms.region === "US" && (
                                                <>
                                                    <FieldRow label="RSSI T-Mobile (dBm)" value={cellComms.rssi_tmobile} valueStyle={{ color: getCellCommsColor(cellComms.rssi_tmobile), fontWeight: 600 }} />
                                                    <FieldRow label="RSSI AT&T (dBm)" value={cellComms.rssi_att} valueStyle={{ color: getCellCommsColor(cellComms.rssi_att), fontWeight: 600 }} />
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
            {Object.keys(sensorNameToStructure).length > 0 && (
                <Tooltip title={
                    !allSensorsConfigured
                        ? "All sensors must have tower type, allowed mast headings, and (for lattice towers) mounting leg filled in"
                        : !isProjectConfigured(projectDetails, { allSensorsLattice, allSensorsNonLattice })
                            ? "Project installation details must be complete before saving"
                            : ""
                }>
                    <span style={{ marginTop: 8 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={!allSensorsConfigured || !isProjectConfigured(projectDetails, { allSensorsLattice, allSensorsNonLattice }) || isCreatingFieldPlan}
                            onClick={handleCreateFieldPlan}
                            startIcon={isCreatingFieldPlan ? <CircularProgress size={14} color="inherit" /> : undefined}
                        >
                            Save Field Plan
                        </Button>
                    </span>
                </Tooltip>
            )}
        </div>
    );
}
